
import Vue from 'vue';
import { State } from 'vuex-class';
import { Component, Prop, Watch } from 'vue-property-decorator';
import DocButton from '@/components/ui/buttons/DocButton.vue';
import CopyCodeButton from '@/components/ui/buttons/CopyCodeButton.vue';
import LanguageSelector from '@/components/ui/LanguageSelector.vue';
import { TutorialContentSourceExample, TutorialContentSourceSDK } from '@/templates/Tutorial';
import {
  keyofLangList,
  MediaSdkPreparedExample,
  PreparedExamplesType,
  SavedExamplesInterface,
  titleOfSdkList,
} from '@/templates/CodeExamples';
import { GlobalBusNames } from '@/templates/DataNames';
import { SelectedLanguagesPack } from '@/store/types';
import GlobalBus from '@/plugins/GlobalBus';
import { convertToAnchorIdHelper, TransformSdkName } from '@/heplers/stringHelpers';
import { CODE_LANG_ENVS } from '@/constants/code-lang-envs';

@Component({
  components: {
    DocButton,
    LanguageSelector,
    CopyCodeButton,
  },
})
export default class CodeExampleHeader extends Vue {
  @Prop() data: TutorialContentSourceSDK;
  @Prop({
    default: () => ({}),
  })
  preparedExamples: PreparedExamplesType;
  @State('selectedLanguagesPack') selectedLanguagesPack: SelectedLanguagesPack;
  @State('pageRendered') pageRendered: boolean;
  public activeSource = '';
  public activeGHLink = '';
  public showScroll = false;
  public showLeftBlur = false;
  public showRightBlur = false;
  public oneLangEnvs = CODE_LANG_ENVS;
  public hightlightArray: string[] = [];
  public langList: keyofLangList[] = []; // array of langs current sdk for language tabs
  public sdkList: titleOfSdkList[] = []; // sdk array for vueselect
  public activeSdk: titleOfSdkList = '';
  public active = false;
  $refs: {
    sdksWrapper: HTMLElement;
    sdkTabs: HTMLElement;
  };

  get titleSdkToKey(): string {
    return TransformSdkName.titleToKey(this.activeSdk);
  }

  get currentLanguage(): keyofLangList {
    //@ts-expect-error: expect error
    if (this.langList.includes(this.selectedLanguagesPack[this.titleSdkToKey])) {
      //@ts-expect-error: expect error
      return this.selectedLanguagesPack[this.titleSdkToKey] === 'c#'
        ? 'csharp'
        : //@ts-expect-error: expect error
          this.selectedLanguagesPack[this.titleSdkToKey];
    } else return this.langList[0];
  }

  toggleExampleCode(): void {
    this.active = !this.active;

    this.$emit('onToogleExampleCode', this.active);
  }

  keySdkToTitle(sdk: string): string {
    let objOfTitles: any = {
      reactnative: 'React Native',
      'web sdk': 'Web',
      'ios sdk': 'IOS',
      'http api': 'Management API',
      'flutter sdk': 'Flutter',
      'reactnative sdk': 'React Native',
      kit: 'KIT',
      voxengine: 'VoxEngine',
      'android sdk': 'Android',
      'cti api': 'CTI API',
    };
    return objOfTitles[sdk.toLowerCase()] || sdk;
  }

  @Watch('preparedExamples')
  onChangePreparedExamples() {
    this.initPrepareData(false);
  }

  beforeDestroy() {
    GlobalBus.off(GlobalBusNames.CHANGESDK);
    GlobalBus.off(GlobalBusNames.NEWLANGUAGEPACKLOADED);
  }

  mounted() {
    GlobalBus.on(GlobalBusNames.CHANGESDK, (savedExample: SavedExamplesInterface) =>
      this.changeActiveSdk(savedExample)
    );
    GlobalBus.on(GlobalBusNames.NEWLANGUAGEPACKLOADED, this.onChangeLanguage);
    window.addEventListener('resize', this.isShowOverflow, { passive: true });
    this.$refs.sdksWrapper.addEventListener('scroll', this.isShowOverflow);
  }

  isShowOverflow(event: any = this.$refs.sdksWrapper) {
    let diff = this.$refs.sdkTabs?.offsetWidth - this.$refs?.sdksWrapper?.offsetWidth;

    // Вычисляем скролл
    this.showScroll = this.$refs.sdksWrapper?.offsetWidth < this.$refs.sdkTabs?.offsetWidth;

    let targetElement = event?.target || event;

    if (!targetElement) return;

    this.showRightBlur = diff - targetElement.scrollLeft >= 40;
    this.showLeftBlur = targetElement.scrollLeft >= 40;
  }

  /**
   * Recompute links only for http_api or mediaSdk, because voxengine always have only lang
   */
  onChangeLanguage() {
    if (!this.preparedExamples || !Object.keys(this.preparedExamples).length) return;

    if (['http_api', 'cti api'].includes(this.data.env))
      this.httpApiComputeCurrentExampleFromLang();

    if (this.data.env === 'media') {
      if (!(this.preparedExamples as MediaSdkPreparedExample)[this.activeSdk]) return;

      // Filter examples for find example with current lang
      const currentExample = (this.preparedExamples as MediaSdkPreparedExample)[
        this.activeSdk
      ].find((example) => example.lang === this.currentLanguage);

      this.prepareLinks(currentExample);
    }
  }

  changeCurrentLang(currentLang: string) {
    this.sendDataToParent(this.activeSdk, currentLang, this.data.uniqId);
  }

  /**
   * Set selected sdk as active, and then change other CodeRailsHeader activeSdks. We change sdk only when if it's media .env
   */
  public setExample(sdk: titleOfSdkList): void {
    let saveExamples: SavedExamplesInterface = this.getSaveExamples();
    this.activeSdk = sdk;
    this.saveExampleSettingsToStore(sdk, saveExamples);

    this.langList = this.computeAvailableLangs(
      (this.preparedExamples as MediaSdkPreparedExample)[this.activeSdk]
    );

    this.sendDataToParent(sdk, this.currentLanguage, this.data.uniqId);
    this.changeAllActiveSdk(saveExamples, sdk);
  }

  /**
   * After compute activeSdk and Langs, send it to CodeExamples component
   */
  sendDataToParent(activeSdk: titleOfSdkList, currentLang: string, uniqId: string) {
    this.$parent.$emit('changeHeaderData', { activeSdk, currentLang, uniqId });
  }

  /**
   * Prepare currentExample for httpApi case
   */
  private httpApiComputeCurrentExampleFromLang(): void {
    let example: TutorialContentSourceExample;

    if (this.currentLanguage === 'curl')
      example =
        //@ts-expect-error: expect error
        this.preparedExamples[this.currentLanguage].script ||
        //@ts-expect-error: expect error
        this.preparedExamples[this.currentLanguage].return;
    else {
      //@ts-expect-error: expect error
      example = this.preparedExamples[
        this.currentLanguage === 'c#' ? 'csharp' : this.currentLanguage
      ];
    }

    this.prepareLinks(example);
  }

  /**
   * Set langList by .env
   */
  private setLangList(env: string): void {
    const envScenarios: any = {
      http_api: this.prepareHttpApiLangs(),
      'cti api': this.prepareHttpApiLangs(),
      voxengine: [(this.preparedExamples as TutorialContentSourceExample).lang],
      javascript: [(this.preparedExamples as TutorialContentSourceExample).lang],
      html: [(this.preparedExamples as TutorialContentSourceExample).lang],
      media: this.computeAvailableLangs(
        (this.preparedExamples as MediaSdkPreparedExample)[this.activeSdk]
      ),
    };

    this.langList = envScenarios[env];
  }

  /**
   * Из-за того что нам приходит csharp а нам нужно показывать c# мы не можем во vue-select показывать другой title, поэтому приходится идти по обратному пути и подменять значение на другое. В итоге в languageSelector мы подмениваем csharp на c#, а тут и в CodeExampleBody подмениваем обратно
   */
  prepareHttpApiLangs() {
    let langs = Object.keys(this.preparedExamples);
    if (langs.includes('csharp')) langs.splice(langs.indexOf('csharp'), 1, 'c#');

    return langs;
  }

  /**
   * Set sdkList and activeSDk by .env
   */
  private setSdks(env: string) {
    const envScenarios: any = {
      http_api: [TransformSdkName.keyToTitle(env)],
      'cti api': [TransformSdkName.keyToTitle(env)],
      media: Object.keys(this.preparedExamples),
    };

    this.sdkList = envScenarios[env];

    this.takeActiveSdk();
  }

  /**
   * Prepare currentObject and set links by .env. Use switch instead object because of we need prevent run all scenarios in values. By default currentObject equals voxengine result
   */
  private setCodeLinks(env: string) {
    let currentObject = <TutorialContentSourceExample>this.preparedExamples;

    if (['http_api', 'cti api'].includes(env))
      currentObject =
        this.langList[0] === 'curl'
          ? // @ts-expect-error: Element implicitly has an 'any' type because expression of type 'curl' can't be used to index type 'PreparedExamplesType'
            this.preparedExamples['curl'].script || this.preparedExamples['curl'].return
          : // @ts-expect-error: Element implicitly has an 'any' type because expression of type
            this.preparedExamples[this.langList?.[0]];
    else if (env === 'media')
      currentObject = (this.preparedExamples as MediaSdkPreparedExample)[this.activeSdk]?.[0];

    this.prepareLinks(currentObject);
  }

  /**
   * Init content by preparedExamples and data.env
   */
  private initPrepareData(fromChangeSdk?: boolean) {
    if (fromChangeSdk === false && !this.oneLangEnvs.includes(this.data.env))
      this.setSdks(this.data.env);

    this.setLangList(this.data.env);

    // If currentLanguage doesn't exist in langList, emit changeLanguage for re-render languageTabs with default langs for this sdk
    if (!this.langList.includes(this.currentLanguage))
      GlobalBus.emit(GlobalBusNames.CHANGELANGUAGE);

    setTimeout(this.isShowOverflow, 500);

    this.setCodeLinks(this.data.env);

    this.sendDataToParent(this.activeSdk, this.currentLanguage, this.data.uniqId);
  }

  /**
   * After GlobalBus.change_active_sdk we change activeSdk
   */
  private changeActiveSdk(saveExamples: SavedExamplesInterface): void {
    this.takeActiveSdk(saveExamples);
    this.initPrepareData(true);
  }

  /**
   * Send event to every CodeRailsHeader component for change active sdk
   */
  private changeAllActiveSdk(
    saveExamples: SavedExamplesInterface,
    activeSdk: titleOfSdkList
  ): void {
    // Меняем activeSdk во всех saveExamples если такое существует
    Object.keys(saveExamples).map((obj: any) => {
      if (saveExamples[obj].sdkList.includes(activeSdk)) saveExamples[obj].activeSdk = activeSdk;
    });

    if (typeof window !== 'undefined') {
      try {
        localStorage.setItem('saveExamples', JSON.stringify(saveExamples));
      } catch (e) {
        console.error('Error while saving item to localStorage');
      }
    }

    GlobalBus.emit(GlobalBusNames.CHANGESDK, saveExamples);
  }

  /**
   * Prepare link for github and codeSource
   */
  private prepareLinks(currentExample: TutorialContentSourceExample) {
    if (!currentExample) return;

    this.activeSource = currentExample.source;
    this.hightlightArray = currentExample.highlight;

    this.computeGhLink(currentExample);
  }

  private computeGhLink(currentExample: TutorialContentSourceExample) {
    if (!currentExample.ghLink) this.activeGHLink = '';

    if (currentExample.ghLink.includes('http')) this.activeGHLink = currentExample.ghLink;
    else
      this.activeGHLink =
        currentExample.ghLink.length > 0
          ? 'https://' + currentExample.ghLink
          : currentExample.ghLink;
  }

  /**
   * Set activeSdk. We get it from localstorage if it has, or we set by default first one
   */
  private takeActiveSdk(examplesFromEvent?: SavedExamplesInterface) {
    const anchorId = this.computeAnchorId();
    const saveExamples: SavedExamplesInterface = examplesFromEvent || this.getSaveExamples();

    if (!saveExamples || !saveExamples[anchorId]) {
      this.activeSdk = this.sdkList[0];
      this.saveExampleSettingsToStore(this.sdkList[0], saveExamples);
    } else this.activeSdk = saveExamples[anchorId].activeSdk;
  }

  /**
   * Get Langs for activeSdk
   */
  private computeAvailableLangs(obj: any): keyofLangList[] {
    if (!obj) return [];

    return obj.reduce((acc: any, value: any) => [...acc, value.lang], []);
  }

  /**
   * Create anchorId for currentExample
   */
  private computeAnchorId() {
    return this.$route.meta.fqdn + '_' + convertToAnchorIdHelper(this.data.uniqId);
  }

  /**
   * Take SaveExamples content from localstorage
   */
  private getSaveExamples(): SavedExamplesInterface {
    let saveExamples: SavedExamplesInterface = {};
    if (typeof window === 'undefined') return saveExamples;

    if (localStorage.getItem('saveExamples'))
      saveExamples = JSON.parse(localStorage.getItem('saveExamples'));

    return saveExamples;
  }

  /**
   * Save current Example to localstorage
   */
  private saveExampleSettingsToStore(
    sdk: titleOfSdkList,
    saveExamples: SavedExamplesInterface
  ): void {
    const anchorId = this.computeAnchorId();

    saveExamples[anchorId] = {
      activeSdk: sdk,
      sdkList: this.sdkList,
    };
    if (typeof window !== 'undefined')
      localStorage.setItem('saveExamples', JSON.stringify(saveExamples));
  }
}
